import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

const STORAGE_COLLAPSED = 'onboarding_checklist_collapsed';

export default function OnboardingChecklist({
  items,
  completedKeys,
  allCompleted,
  onNavigate,
  onDismiss,
  title,
  doneTitle,
  doneMessage,
  doneButton,
  doneRoute = '/schedule'
}) {
  const { t } = useTranslation(['onboarding']);
  const resolvedTitle = title ?? t('onboarding:checklistOwnerTitle');
  const resolvedDoneTitle = doneTitle ?? t('onboarding:checklistOwnerDone');
  const resolvedDoneMessage = doneMessage ?? t('onboarding:ownerChecklistDoneMessage');
  const resolvedDoneButton = doneButton ?? t('onboarding:ownerChecklistDoneButton');
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_COLLAPSED) === '1'
  );

  const doneCount = completedKeys.length;
  const total = items.length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  const nextKey = useMemo(
    () => items.find((i) => !completedKeys.includes(i.key))?.key,
    [items, completedKeys]
  );

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(STORAGE_COLLAPSED, next ? '1' : '0');
  }

  function go(item) {
    if (onNavigate) onNavigate(item);
    else navigate(item.route);
  }

  if (allCompleted) {
    return (
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 9000,
          background: '#fff',
          borderRadius: 12,
          padding: 20,
          width: 300,
          boxShadow: '0 8px 32px rgba(26,35,126,0.16)',
          textAlign: 'center'
        }}
      >
        <div style={{ fontSize: 32, color: '#43a047' }}>✓</div>
        <div style={{ fontWeight: 600, marginTop: 8 }}>{resolvedDoneTitle}</div>
        <p style={{ fontSize: 13, color: '#616161' }}>{resolvedDoneMessage}</p>
        <button
          type="button"
          onClick={() => {
            confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
            navigate(doneRoute);
          }}
          style={{
            marginTop: 8,
            padding: '10px 16px',
            background: '#EB3333',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer'
          }}
        >
          {resolvedDoneButton}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        zIndex: 9000,
        background: '#fff',
        borderRadius: 12,
        width: 320,
        boxShadow: '0 8px 32px rgba(26,35,126,0.16)',
        overflow: 'hidden',
        fontFamily: 'inherit'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          cursor: 'pointer'
        }}
        onClick={toggleCollapsed}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{resolvedTitle}</div>
          <div style={{ fontSize: 12, color: '#757575' }}>
            {t('onboarding:checklistProgress', { done: doneCount, total })}
          </div>
        </div>
        <span style={{ color: '#9e9e9e' }}>{collapsed ? '▼' : '─'}</span>
      </div>
      {!collapsed ? (
        <>
          <div style={{ height: 4, background: '#e0e0e0' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: '#EB3333' }} />
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: '8px 0', maxHeight: 280, overflow: 'auto' }}>
            {items.map((item) => {
              const done = completedKeys.includes(item.key);
              const active = item.key === nextKey;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => !done && go(item)}
                    style={{
                      display: 'flex',
                      width: '100%',
                      textAlign: 'left',
                      gap: 8,
                      padding: '10px 16px',
                      border: 'none',
                      background: active ? '#e8eaf6' : 'transparent',
                      cursor: done ? 'default' : 'pointer',
                      fontSize: 13,
                      color: done ? '#9e9e9e' : active ? '#EB3333' : '#212121'
                    }}
                  >
                    <span>{done ? '✓' : active ? '→' : '○'}</span>
                    <span style={{ textDecoration: done ? 'line-through' : 'none' }}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {onDismiss ? (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('onboarding:dismissChecklistMessageWeb'))) {
                  onDismiss();
                }
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 16px',
                border: 'none',
                borderTop: '1px solid #eee',
                background: 'transparent',
                color: '#9e9e9e',
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              {t('onboarding:dismissChecklist')}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
